import { useState } from "react";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
}

interface DeleteAdminUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AdminUser;
  onSuccess: () => void;
}

export function DeleteAdminUserModal({ open, onOpenChange, user, onSuccess }: DeleteAdminUserModalProps) {
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  const confirmationText = 'EXCLUIR ADMIN';

  const handleDelete = async () => {
    if (confirmation !== confirmationText) return;

    try {
      setDeleting(true);

      const { error } = await supabase.functions.invoke(
        'admin-delete-user',
        { body: { userId: user.id } }
      );

      if (error) throw error;

      toast.success('Usuário admin removido com sucesso!');
      onSuccess();
      onOpenChange(false);

    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao excluir usuário');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Excluir Usuário Admin
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <p>Você está prestes a excluir o acesso administrativo de:</p>
            <div className="font-mono text-sm bg-muted p-3 rounded">
              <p className="font-semibold">{user.full_name || 'Sem nome'}</p>
              <p className="text-muted-foreground">{user.email}</p>
            </div>

            <Alert variant="destructive">
              <AlertDescription>
                <strong>ATENÇÃO:</strong> Esta ação é permanente e não pode ser desfeita.
                O usuário perderá todo acesso ao painel administrativo e será completamente removido do sistema.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>Digite "{confirmationText}" para confirmar:</Label>
              <Input
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder={confirmationText}
                disabled={deleting}
                className="font-mono"
              />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={confirmation !== confirmationText || deleting}
          >
            {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Excluir Usuário Admin
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
